import React, { useEffect } from "react";
import {
  Button,
  Typography,
  makeStyles,
  FormControl,
  InputAdornment,
  OutlinedInput,
  InputLabel,
  IconButton,
} from "@material-ui/core";
import { useCart } from "../Context/CartContext";
import { formatFinalAmount } from "../utils/currency";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import BackspaceOutlinedIcon from "@material-ui/icons/BackspaceOutlined";

const useStyles = makeStyles((theme) => ({
  cashPanel: {
    width: "min(640px, calc(100% - 32px))",
    margin: "42px auto 36px",
    padding: theme.spacing(2, 3, 3),
    alignSelf: "flex-start",
    borderRadius: 20,
    background: "#ffffff",
    border: "1.5px solid #e2e8f0",
    boxShadow: "0 22px 50px rgba(15, 23, 42, 0.12)",
    [theme.breakpoints.down("xs")]: {
      width: "calc(100% - 20px)",
      margin: "24px 10px 30px",
      padding: theme.spacing(1.5, 1.5, 2),
      borderRadius: 16,
    },
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
  },
  backButton: {
    width: 38,
    height: 38,
    borderRadius: 10,
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#475569",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "#f1f5f9",
      color: "#0f172a",
      borderColor: "#cbd5e1",
    },
    "& .MuiSvgIcon-root": {
      fontSize: 16,
      marginLeft: 4, // optical centering for ArrowBackIos
    },
  },
  headerTitle: {
    fontSize: "1.05rem",
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  },
  headerPlaceholder: {
    width: 38,
  },
  totalDisplayCard: {
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: "10px 16px 12px",
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  totalDisplayLabel: {
    display: "block",
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#64748b",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 2,
  },
  changeText: {
    textAlign: "center",
    fontSize: "clamp(2rem, 4.5vw, 2.75rem)",
    fontWeight: 800,
    color: "#0f172a",
    margin: 0,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
  },
  cashFields: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: theme.spacing(1.25),
    },
  },
  receivedAmountField: {
    flex: 1,
    "& .MuiInputLabel-outlined": {
      fontSize: "0.9rem",
      fontWeight: 600,
      color: "#64748b",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#ffffff",
      border: "1px solid #e2e8f0",
      transition: "all 0.18s ease",
      "&.Mui-focused": {
        borderColor: "#f97316",
        boxShadow: "0 0 0 3px rgba(249, 115, 22, 0.15)",
      },
      "& input": {
        padding: "12px 14px",
        fontSize: "1.5rem",
        fontWeight: 700,
        color: "#0f172a",
        fontVariantNumeric: "tabular-nums",
      },
    },
  },
  changeField: {
    flex: 1,
    "& .MuiInputLabel-outlined": {
      fontSize: "0.9rem",
      fontWeight: 600,
      color: "#64748b",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      "&.changePositive": {
        backgroundColor: "#f0fdf4",
        borderColor: "#86efac",
        "& input": {
          color: "#15803d",
        },
      },
      "& input": {
        padding: "12px 14px",
        fontSize: "1.5rem",
        fontWeight: 700,
        color: "#334155",
        fontVariantNumeric: "tabular-nums",
      },
    },
  },
  currencyBadge: {
    fontSize: "1.1rem",
    fontWeight: 800,
    color: "#64748b",
    backgroundColor: "#f1f5f9",
    padding: "2px 8px",
    borderRadius: 6,
    marginRight: 4,
  },
  currencyBadgeActive: {
    color: "#ea580c",
    backgroundColor: "#fff7ed",
  },
  currencyBadgePositive: {
    color: "#15803d",
    backgroundColor: "#dcfce7",
  },
  dialPadContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(1),
    marginBottom: 0,
  },
  dialButton: {
    fontSize: "1.55rem",
    fontWeight: 700,
    minHeight: 52,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    background: "#ffffff",
    color: "#1e293b",
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 5px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.12s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: "#fff7ed",
      borderColor: "#fdba74",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 10px rgba(15, 23, 42, 0.08)",
    },
    "&:active": {
      transform: "scale(0.96)",
      background: "#ffedd5",
    },
    [theme.breakpoints.down("xs")]: {
      minHeight: 46,
      fontSize: "1.35rem",
    },
  },
  backspaceButton: {
    background: "#f8fafc",
    color: "#475569",
    borderColor: "#e2e8f0",
    "&:hover": {
      background: "#f1f5f9",
      borderColor: "#cbd5e1",
      color: "#0f172a",
    },
    "&:active": {
      background: "#e2e8f0",
    },
  },
  clearButton: {
    gridColumn: "span 2",
    borderRadius: 12,
    background: "#fef2f2",
    color: "#dc2626",
    border: "1.5px solid #fecaca",
    fontWeight: 700,
    fontSize: "0.95rem",
    letterSpacing: "0.01em",
    "&:hover": {
      background: "#fee2e2",
      borderColor: "#f87171",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "scale(0.97)",
    },
  },
  payButton: {
    gridColumn: "span 1",
    minHeight: 52,
    margin: 0,
    borderRadius: 12,
    background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
    color: "#ffffff",
    fontWeight: 800,
    fontSize: "1rem",
    textTransform: "none",
    letterSpacing: "0.01em",
    boxShadow: "0 8px 20px rgba(234, 88, 12, 0.32)",
    transition: "all 0.15s ease",
    "&:hover": {
      background: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)",
      boxShadow: "0 10px 24px rgba(234, 88, 12, 0.42)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "scale(0.97)",
    },
    [theme.breakpoints.down("xs")]: {
      minHeight: 46,
      fontSize: "0.9rem",
    },
  },
}));

function BillDialog({ open, onClose, currency, setOpenBillDialog, invoice, allowPartialPay }) {
  const classes = useStyles();
  const { getTotalPrice, getRoundOff, receivedAmount, setReceivedAmount, calculateChange } =
    useCart();

  // Pre-fills with the exact bill total so the common case (customer pays
  // exact change) needs no typing at all - the cashier can still edit it up
  // (change is due back) or down for a partial cash amount.
  useEffect(() => {
    if (invoice) {
      const prefill =
        invoice.receivedAmount != null && invoice.receivedAmount !== ""
          ? invoice.receivedAmount
          : invoice.totalAmountReceived;
      setReceivedAmount(prefill != null && prefill !== "" ? String(prefill) : "");
    } else {
      setReceivedAmount(formatFinalAmount(getTotalPrice()));
    }
    // eslint-disable-next-line
  }, [invoice]);

  const handleDialButtonClick = (digit) => {
    setReceivedAmount((prev) => String(prev) + digit);
  };

  const handleClearAll = () => {
    setReceivedAmount("");
  };

  const handleBackspace = () => {
    setReceivedAmount((prev) => String(prev).slice(0, -1));
  };

  const handleDecimalClick = () => {
    setReceivedAmount((prev) => {
      const str = String(prev);
      if (str.includes(".")) return str;
      return (str === "" ? "0" : str) + ".";
    });
  };

  // Keeps typed input to digits + a single "." so manual entry behaves the
  // same as the on-screen dial pad (also brings up a numeric keyboard with
  // a dot on mobile via inputMode="decimal" below).
  const sanitizeAmountInput = (value) => {
    let v = String(value).replace(/[^0-9.]/g, "");
    const firstDot = v.indexOf(".");
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
    }
    return v;
  };

  const rawChange = calculateChange();
  const billTotal = Number(getTotalPrice()) || 0;
  const received = parseFloat(receivedAmount) || 0;
  const remainingDue =
    allowPartialPay && received < billTotal ? (billTotal - received).toFixed(2) : "";
  const showBalanceDue = Boolean(remainingDue);
  const isChangePositive =
    !showBalanceDue &&
    (typeof rawChange === "number" ? rawChange > 0 : parseFloat(rawChange) > 0);
  const secondFieldValue = showBalanceDue ? remainingDue : rawChange;
  const secondFieldLabel = showBalanceDue ? "Balance Due" : "Change Due";

  return (
    <div className={classes.cashPanel}>
      <div className={classes.headerRow}>
        <IconButton
          className={classes.backButton}
          onClick={() => setOpenBillDialog(false)}
          title="Back to payment options"
        >
          <ArrowBackIosIcon />
        </IconButton>
        <Typography className={classes.headerTitle}>Cash Payment</Typography>
        <div className={classes.headerPlaceholder} />
      </div>

      <div className={classes.totalDisplayCard}>
        <span className={classes.totalDisplayLabel}>Bill Total Due</span>
        <Typography className={classes.changeText}>
          {`${currency}${formatFinalAmount(getTotalPrice())}`}
        </Typography>
        {getRoundOff && getRoundOff() !== 0 && (
          <span className={classes.totalDisplayLabel} style={{ marginTop: 4 }}>
            {`(incl. round off ${getRoundOff() > 0 ? "+" : "-"}${currency}${Math.abs(getRoundOff()).toFixed(2)})`}
          </span>
        )}
      </div>

      <div className={classes.cashFields}>
        <FormControl className={classes.receivedAmountField} variant="outlined">
          <InputLabel htmlFor="outlined-adornment-amount">Amount Received</InputLabel>
          <OutlinedInput
            id="outlined-adornment-amount"
            value={receivedAmount}
            onChange={(e) => setReceivedAmount(sanitizeAmountInput(e.target.value))}
            inputProps={{ inputMode: "decimal" }}
            startAdornment={
              <InputAdornment position="start">
                <span className={`${classes.currencyBadge} ${classes.currencyBadgeActive}`}>
                  {currency}
                </span>
              </InputAdornment>
            }
            labelWidth={125}
          />
        </FormControl>

        <FormControl className={classes.changeField} variant="outlined">
          <InputLabel htmlFor="outlined-adornment-change">{secondFieldLabel}</InputLabel>
          <OutlinedInput
            id="outlined-adornment-change"
            value={secondFieldValue}
            disabled
            className={isChangePositive ? "changePositive" : ""}
            startAdornment={
              <InputAdornment position="start">
                <span
                  className={`${classes.currencyBadge} ${
                    isChangePositive ? classes.currencyBadgePositive : ""
                  }`}
                >
                  {currency}
                </span>
              </InputAdornment>
            }
            labelWidth={showBalanceDue ? 95 : 85}
          />
        </FormControl>
      </div>

      <div className={classes.dialPadContainer}>
        {Array.from(Array(9).keys()).map((number) => (
          <div
            key={number}
            className={classes.dialButton}
            onClick={() => handleDialButtonClick((number + 1).toString())}
          >
            {number + 1}
          </div>
        ))}
        <div
          className={classes.dialButton}
          onClick={() => handleDialButtonClick("0")}
        >
          0
        </div>
        <div className={classes.dialButton} onClick={handleDecimalClick}>
          .
        </div>
        <div
          className={`${classes.dialButton} ${classes.backspaceButton}`}
          onClick={handleBackspace}
          title="Backspace"
        >
          <BackspaceOutlinedIcon style={{ fontSize: 22 }} />
        </div>
        <div
          className={`${classes.dialButton} ${classes.clearButton}`}
          onClick={handleClearAll}
        >
          Clear All
        </div>
        <Button variant="contained" type="submit" className={classes.payButton}>
          {invoice ? "Update" : "Pay Now"}
        </Button>
      </div>
    </div>
  );
}

export default BillDialog;
